import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";
import { SolomonVault } from "@solomon-labs/solomon-protocol";
import { create } from "zustand";

import type { AssetExchangeRates, VaultState } from "../types/vault";

/**
 * Vault store actions
 */
interface VaultActions {
    initialize: () => Promise<void>;
    getVaultState: () => Promise<void>;
    reset: () => void;
}

export type VaultStore = VaultState & VaultActions;

/**
 * Initial state
 */
const INITIAL_STATE: VaultState = {
    vaultTokenMint: undefined,
    assetManagers: [],
    roleManagers: [],
    withdrawAddresses: [],
    rates: {},
    vaultTokenSupply: undefined,
    isVaultStateLoaded: false,
    isError: false
};

/**
 * Vault store for managing mint/redeem vault state
 * - Vault token configuration
 * - Exchange rates for supported assets
 * - Manager permissions
 */
export const useVaultStore = create<VaultStore>((set) => ({
    ...INITIAL_STATE,

    /**
     * Initialize the store
     */
    initialize: async (): Promise<void> => {
        // No initialization needed
    },

    /**
     * Reset store to initial state
     */
    reset: (): void => {
        set(INITIAL_STATE);
    },

    /**
     * Fetch vault state from on-chain program using SolomonVault SDK
     */
    getVaultState: async (): Promise<void> => {
        // Set loading state
        set({ isVaultStateLoaded: false, isError: false });

        try {
            // Fetch vault state using SolomonVault SDK
            const vaultState = await SolomonVault.fetchVaultState();

            // Fetch all exchange rates (returns Map<PublicKeyLike, ExchangeRate>)
            const supportedAssets = await SolomonVault.getSupportedAssets();

            // Build rates map for our store format
            const rates: AssetExchangeRates = {};
            for (const [assetKey, exchangeRate] of supportedAssets) {
                const assetAddress = assetKey.toString();
                rates[assetAddress] = {
                    asset: exchangeRate.asset as PublicKey,
                    depositRate: new BigDecimal(exchangeRate.depositRate.toString()),
                    redeemRate: new BigDecimal(exchangeRate.redeemRate.toString())
                };
            }

            set({
                vaultTokenMint: vaultState.vaultTokenMint as PublicKey,
                assetManagers: vaultState.assetManagers as PublicKey[],
                roleManagers: vaultState.roleManagers as PublicKey[],
                withdrawAddresses: vaultState.withdrawAddresses as PublicKey[],
                rates,
                vaultTokenSupply: undefined, // TODO: Fetch vault token supply if needed
                isVaultStateLoaded: true,
                isError: false
            });
        } catch (error) {
            console.error("Error fetching vault state:", error);
            set({
                isVaultStateLoaded: true,
                isError: true
            });
        }
    }
}));
